/* LUCRÜ™ — © 2026 LUCRÜ. All rights reserved. */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { SpinnerGapIcon, LightningIcon } from "@phosphor-icons/react";
import { api, getOrCreateSession } from "@/lib/api";
import Logo from "@/components/Logo";

/**
 * 1-click deep subscribe route: /subscribe/:lookup_key
 * Immediately creates a Stripe Checkout session and redirects the user.
 * Any potential user can be sent this URL from any social channel.
 */
const Subscribe = () => {
  const { lookupKey } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("Preparing your 30-day free trial…");
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const sessionId = getOrCreateSession();
        setStatus("Opening secure Stripe checkout…");
        const r = await api.post("/billing/checkout", {
          session_id: sessionId,
          lookup_key: lookupKey,
          origin_url: window.location.origin,
        });
        window.location.href = r.data.checkout_url;
      } catch (e) {
        const msg = e?.response?.data?.detail || "Checkout failed";
        setError(msg);
        toast.error(msg);
      }
    })();
  }, [lookupKey]);

  return (
    <div className="min-h-screen bg-[#05050A] text-white flex items-center justify-center px-6">
      <motion.div
        className="glass rounded-2xl p-8 max-w-md w-full text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex justify-center mb-5">
          <Logo size={64} withBackground className="glow-primary" />
        </div>
        <div className="font-display font-black text-2xl tracking-tight mb-2">Welcome to LUCRÜ</div>
        <div className="text-xs text-white/40 tracking-[0.3em] uppercase mb-6">by LUCRÜ</div>

        {!error ? (
          <>
            <div className="flex items-center justify-center gap-2 text-sm text-white/70 mb-6">
              <SpinnerGapIcon size={16} className="animate-spin" />
              <span>{status}</span>
            </div>
            <div className="text-xs text-white/40">
              You're one click from your AI voice co-host — 30 days free, cancel anytime.
            </div>
          </>
        ) : (
          <>
            <div className="text-sm text-[#FF007F] mb-6">{error}</div>
            <button onClick={() => navigate("/pricing")} className="btn-pill btn-primary w-full justify-center">
              <LightningIcon size={14} weight="fill" /> See all plans
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default Subscribe;
